import { useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  MODE_DESCRIPTIONS,
  MODE_LABELS,
  MODES,
  type EmailCategory,
  type TriageMode,
} from '@/shared/triage/vocabulary';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';

import type { ModeEntry, TriageActivation } from './types';
import { useAdminTriage, useAllShadow, usePutModes } from './useAdmin';

/**
 * How much the system does on its own, per mailbox and category, editable. And the one
 * button that matters on a bad day: «Todo a solo proponer».
 *
 * A change here is written at once (each select saves its pair), because the person
 * changing «facturas de proveedor a solo» is doing exactly one thing and should not have
 * to find a save button for it. The state of the lock is one line above the matrix; what
 * each mode means and what the contract says about «solo» is in the header's help.
 */
export function ActivationTab() {
  const triage = useAdminTriage();
  const allShadow = useAllShadow();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      {triage.isPending ? <Spinner variant="block" label="Cargando la activación" /> : null}
      {triage.isError ? (
        <ErrorMessage error={triage.error}>No se pudo cargar la activación</ErrorMessage>
      ) : null}
      {triage.data ? (
        <>
          <Callout
            compact
            tone={triage.data.enabled ? 'warn' : 'info'}
            title={triage.data.enabled ? 'El cerrojo está abierto.' : 'El cerrojo está cerrado.'}
            role="status"
            action={
              confirming ? (
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={allShadow.isPending}
                    onClick={() =>
                      allShadow.mutate(undefined, { onSuccess: () => setConfirming(false) })
                    }
                  >
                    {allShadow.isPending ? 'Parando…' : 'Sí, todo a solo proponer'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
                    No
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
                  Todo a solo proponer
                </Button>
              )
            }
          >
            {triage.data.enabled
              ? 'Cada tipo de correo hace lo que dice su modo.'
              : 'El sistema clasifica y propone, y no toca nada, digan lo que digan los modos de abajo. Se abre desde el servidor, no desde aquí.'}
          </Callout>
          {allShadow.isError ? (
            <p role="alert" className="text-danger text-[13.5px]">
              {errorMessage(allShadow.error)}
            </p>
          ) : null}
          <ModesMatrix activation={triage.data} />
        </>
      ) : null}
    </div>
  );
}

function ModesMatrix({ activation }: { activation: TriageActivation }) {
  const put = usePutModes();
  const mailboxes = activation.mailboxes ?? [];
  const byPair = new Map<string, ModeEntry>();
  for (const entry of activation.modes ?? [])
    byPair.set(`${entry.mailbox}/${entry.category}`, entry);

  function change(
    mailbox: string,
    category: EmailCategory,
    knob: 'forward' | 'label',
    mode: TriageMode,
  ) {
    const current = byPair.get(`${mailbox}/${category}`);
    put.mutate({
      entries: [
        {
          mailbox,
          category,
          forward_mode:
            knob === 'forward' ? mode : (current?.forward_mode ?? activation.default_forward_mode),
          label_mode:
            knob === 'label' ? mode : (current?.label_mode ?? activation.default_label_mode),
        },
      ],
    });
  }

  const defaults = (
    <span className="text-ink-3 text-[13px]">
      Por defecto: reenviar{' '}
      <strong className="text-ink-2 font-medium">
        «{MODE_LABELS[activation.default_forward_mode]}»
      </strong>
      , copiar a carpeta{' '}
      <strong className="text-ink-2 font-medium">
        «{MODE_LABELS[activation.default_label_mode]}»
      </strong>
      .
    </span>
  );

  const description = (
    <>
      Cuánto hace el sistema por su cuenta, por buzón y tipo de correo, al reenviar y al copiar a
      carpeta. <strong className="text-ink-2 font-medium">Solo propone</strong>: clasifica y
      propone, sin tocar el correo. <strong className="text-ink-2 font-medium">Al confirmar</strong>
      : actúa cuando alguien pulsa «Correcto» o «Corregir a…» en la Bandeja.{' '}
      <strong className="text-ink-2 font-medium">Automático</strong>: actúa en cuanto clasifica el
      correo, sin que nadie lo revise. Cada cambio se guarda al momento.
    </>
  );

  if (mailboxes.length === 0) {
    return (
      <Card title="Cuánto hace el sistema por su cuenta" description={description} aside={defaults}>
        <p className="text-ink-3 text-[14px]">
          Ningún buzón conocido todavía. Cuando uno ingiera correo o se registre en «Buzones», sus
          categorías aparecerán aquí.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Cuánto hace el sistema por su cuenta" description={description} aside={defaults}>
      <div className="flex flex-col gap-5">
        {mailboxes.map((mailbox) => (
          <div key={mailbox} className="overflow-x-auto">
            <p className="text-ink mb-1.5 text-[14.5px] font-medium">{mailbox}</p>
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-ink-3 text-left text-[11.5px] font-semibold tracking-[0.08em] uppercase">
                  <th className="py-1 pr-3 font-semibold">Tipo de correo</th>
                  <th className="py-1 pr-3 font-semibold">Reenviar</th>
                  <th className="py-1 font-semibold">Copiar a carpeta</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((category) => {
                  const entry = byPair.get(`${mailbox}/${category}`);
                  const forward = entry?.forward_mode ?? activation.default_forward_mode;
                  const label = entry?.label_mode ?? activation.default_label_mode;
                  return (
                    <tr key={category} className="border-line-2 border-t">
                      <td className="py-1 pr-3 align-middle">
                        {CATEGORY_LABELS[category]}
                        {entry?.explicit ? (
                          <span
                            className="text-ink-4 ml-1 text-[12px]"
                            title="Distinto de lo que hay por defecto"
                          >
                            (ajustado)
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1 pr-3 align-middle">
                        <ModeSelect
                          label={`Reenviar ${CATEGORY_LABELS[category]} de ${mailbox}`}
                          value={forward}
                          disabled={put.isPending}
                          onChange={(mode) => change(mailbox, category, 'forward', mode)}
                        />
                      </td>
                      <td className="py-1 align-middle">
                        <ModeSelect
                          label={`Copiar a carpeta ${CATEGORY_LABELS[category]} de ${mailbox}`}
                          value={label}
                          disabled={put.isPending}
                          onChange={(mode) => change(mailbox, category, 'label', mode)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        {put.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(put.error)}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

function ModeSelect({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: TriageMode;
  disabled: boolean;
  onChange: (mode: TriageMode) => void;
}) {
  return (
    <select
      aria-label={label}
      className="field h-8 w-auto py-0 text-[13px]"
      value={value}
      disabled={disabled}
      title={MODE_DESCRIPTIONS[value]}
      onChange={(event) => onChange(event.target.value as TriageMode)}
    >
      {MODES.map((mode) => (
        <option key={mode} value={mode}>
          {MODE_LABELS[mode]}
        </option>
      ))}
    </select>
  );
}
